import { Vector2, Vector3, Plane, BoxGeometry, MeshBasicMaterial, Mesh } from 'three';
import { ReactiveComponent } from '../../core/ReactiveComponent.core.js';
import { computeProjectiveMatrix3d } from '../../plugins/3d-2d-transform/src/projectiveMatrix.js';
import { vapiPerspectiveCornerHandleStyle } from '../../constants/vapiPerspectiveHandles.js';

/**
 * VRVapiButtonWithCSS3D - VAPI action button component.
 * Uses 2D DOM positioned like indicatorsArray (get2DSpaceCoordinates), not CSS3DObject.
 * Button component with optional uploaded background image and perspective corner warping.
 * dbConfig.action is textarea content sent as VAPI text command.
 * Child of VRVapiChairWithCSS3D - removed when chair is removed; can be removed separately.
 */
class VRVapiButtonWithCSS3D extends ReactiveComponent {
    _t(key, fallback) {
        if (typeof $trans !== 'function') return fallback || key;
        const value = $trans(key, {}, fallback || key);
        return value || fallback || key;
    }

    constructor(params = {}) {
        super(null, {
            mode: params.editMode ? 'edit' : 'view'
        });

        const { model, camera, controls, raycaster, position, id, dbConfig, sizes } = params;
        this.id = id;
        this.model = model;
        this.camera = camera;
        this.controls = controls || null;
        this.position = this._resolveInitialPosition(position);
        this.dbConfig = dbConfig || {};
        this._raycaster = raycaster || null;
        this._sizes = sizes || { width: window.innerWidth, height: window.innerHeight };
        this._viewportEl = params.viewportElement || document.querySelector('#webgl') || document.querySelector('#css3d');
        this._ndc = new Vector2();
        this._dragPlane = new Plane();
        this._planeIntersectPoint = new Vector3();
        this._tmpVec = new Vector3();
        this._dragStartDistance = 0;
        this._dragOffsetX = 0;
        this._dragOffsetY = 0;
        this.isDragging = false;
        this._captureEl = null;
        this.parent = this;
        this.name = 'vapiButton';
        this.type = 'vapiButton';
        this._cornerHandles = [];

        this._buildNode();
        this.anchor.position.set(
            this.position.x,
            this.position.y,
            this.position.z !== undefined ? this.position.z : -900
        );
        this.anchor.userData.vraiAgentButton = this;

        // Keep command actions non-visual in all modes.
        this._setButtonVisible(false);
        this._setButtonEnabled(false);
        if (this.dom) {
            this.dom.style.display = 'none';
            this.dom.style.pointerEvents = 'none';
            this.dom.style.opacity = '0';
            this.dom.classList.add('hide-on-start', 'hidden-by-action', 'vapi-button-call-inactive');
            this.dom.classList.remove('visible', 'shown-by-action');
        }

        return this;
    }

    _resolveInitialPosition(position) {
        const hasExplicitXYZ = position
            && Number.isFinite(position.x)
            && Number.isFinite(position.y)
            && Number.isFinite(position.z);
        if (hasExplicitXYZ) {
            return { x: Number(position.x), y: Number(position.y), z: Number(position.z) };
        }
        if (!this.camera) return { x: 0, y: 0, z: -900 };
        const dir = new Vector3();
        this.camera.getWorldDirection(dir);
        const worldPos = this.camera.position.clone().addScaledVector(dir, 900);
        return { x: worldPos.x, y: worldPos.y, z: worldPos.z };
    }

    _setButtonEnabled(enabled) {
        const btn = this._buttonEl;
        if (btn) {
            btn.disabled = !enabled;
            btn.setAttribute('aria-disabled', String(!enabled));
            btn.classList.toggle('disabled', !enabled);
        }
    }

    _setButtonVisible(visible) {
        const root = this.dom;
        if (root && this.state.mode !== 'edit') {
            const blockedByHideOnStart = !!this.dbConfig?.hideOnStart && !root.classList.contains('shown-by-action');
            const blockedByHideAction = root.classList.contains('hidden-by-action');
            const shouldForceHidden = !visible || blockedByHideOnStart || blockedByHideAction;
            root.classList.toggle('vapi-button-call-inactive', shouldForceHidden);
        }
    }

    _getOwningChair() {
        const chairId = this.dbConfig?.vapiAgentId ?? null;
        const chairs = this.model?.view?.vraiAgentChairs || [];
        if (!chairId || !Array.isArray(chairs)) return null;
        return chairs.find(c => String(c.getId?.() ?? c.id) === String(chairId)) || null;
    }

    _getMenuDisplayName() {
        const t = typeof $trans !== 'undefined' && $trans('vapi-command-action');
        const componentName = t?.text || t || this._t('vapi-command-action-fallback', 'Command Action');
        const chair = this._getOwningChair();
        const ownerName = (chair?.dbConfig?.chairName || chair?.dbConfig?.name || '').trim();
        const buttonName = (this.dbConfig?.commandName || this.dbConfig?.label || this.dbConfig?.name || '').trim();
        if (ownerName && buttonName) return `${ownerName} - ${componentName}: ${buttonName}`;
        if (ownerName) return `${ownerName} - ${componentName}`;
        return buttonName ? `${componentName}: ${buttonName}` : componentName;
    }

    _setupVapiConnectionListeners() { }

    _disposeVapiConnectionListeners() { }

    _buildNode() {
        const w = Number(this.dbConfig?.width) > 0 ? Number(this.dbConfig.width) : 120;
        const h = Number(this.dbConfig?.height) > 0 ? Number(this.dbConfig.height) : 40;
        this._baseWidth = w;
        this._baseHeight = h;
        this._originalPos = [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }];
        const labelText = (this.dbConfig?.commandName ?? this.dbConfig?.label ?? this.dbConfig?.name ?? '').trim() || this._t('command-text', 'Command');

        const dataId = `vapi_button2_${this.id}`;
        // Ensure a single DOM node per component id to prevent visual doubling.
        const staleNodes = Array.from(document.querySelectorAll(`[data-id='${dataId}']`));
        staleNodes.forEach(node => node?.remove?.());

        const root = document.createElement('div');
        root.setAttribute('class', 'vrai-agent-component video_element vapi_button2_wrapper vrai-agent-2d-indicator vapi-button2d-indicator');
        root.setAttribute('data-css3d-interactive', 'true');
        root.setAttribute('data-id', dataId);
        root.style.cssText = `
            position: fixed;
            width: ${w}px;
            height: ${h}px;
            top: 50%;
            left: 50%;
            margin-left: -${w / 2}px;
            margin-top: -${h / 2}px;
            pointer-events: auto;
            display: none;
            opacity: 0;
            transform-origin: 50% 50%;
            user-select: none;
            -webkit-user-select: none;
        `;
        root.style.opacity = '0';
        root.classList.add(`vapi-button2d-${this.id}`);
        if (this.dbConfig?.hideOnStart) {
            root.classList.add('hide-on-start');
        }

        root.addEventListener('pointerdown', (e) => {
            if (e.button !== 0) return;
            const hitEditableSurface = e.target?.closest?.('.vapi-button2-perspective-content, .vapi-button2-drag-handle')
                ?? document.elementFromPoint(e.clientX, e.clientY)?.closest?.('.vapi-button2-perspective-content, .vapi-button2-drag-handle');
            if (this.state.mode === 'edit' && this._onPointerDown && hitEditableSurface) {
                e.preventDefault();
                e.stopPropagation();
                this._onPointerDown(e);
            }
        });
        // Prevent synthesized click after drag from bubbling to scene deselection handlers.
        root.addEventListener('click', (e) => e.stopPropagation());

        const buttonContent = document.createElement('button');
        buttonContent.className = 'vapi-button2-content vrai-agent-btn-action vrai-btn-end';
        buttonContent.type = 'button';
        buttonContent.style.cssText = `
            position: absolute;
            width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
        `;

        const saved = this.dbConfig?.perspectiveOffsets;
        this._perspectiveCorners = Array.isArray(saved) && saved.length === 4
            ? saved.map(p => p.xr !== undefined
                ? { x: Number(p.xr) * w, y: Number(p.yr) * h }
                : { x: Number(p.x) ?? p.x, y: Number(p.y) ?? p.y })
            : this._originalPos.map(p => ({ ...p }));

        const perspectiveContent = document.createElement('div');
        perspectiveContent.className = 'vapi-button2-perspective-content';
        perspectiveContent.style.cssText = 'position:absolute;inset:0;overflow:visible;transform-origin:0 0;transform-style:flat;will-change:transform;pointer-events:auto;';
        perspectiveContent.appendChild(buttonContent);
        this._perspectiveContent = perspectiveContent;
        this._updateButtonContent(buttonContent, labelText, this.dbConfig?.icon);

        const perspectiveWrapper = document.createElement('div');
        perspectiveWrapper.className = 'vapi-button2-perspective-wrapper';
        perspectiveWrapper.style.cssText = 'position:absolute;inset:0;overflow:visible;pointer-events:auto;';
        perspectiveWrapper.appendChild(perspectiveContent);
        this._perspectiveWrapper = perspectiveWrapper;

        this._applyPerspectiveTransform = () => {
            const m = computeProjectiveMatrix3d(this._originalPos, this._perspectiveCorners);
            perspectiveContent.style.transform = m;
        };
        this._applyPerspectiveTransform();

        if (this.state.mode === 'edit') {
            for (let i = 0; i < 4; i++) {
                const hnd = document.createElement('div');
                hnd.className = 'vapi-button2-corner-handle';
                hnd.dataset.corner = String(i);
                hnd.style.cssText = vapiPerspectiveCornerHandleStyle('pointer-events:auto;');
                const updateHandlePos = () => {
                    hnd.style.left = `${this._perspectiveCorners[i].x}px`;
                    hnd.style.top = `${this._perspectiveCorners[i].y}px`;
                };
                updateHandlePos();
                const onCornerPointerDown = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    hnd.style.cursor = 'grabbing';
                    const startX = e.clientX;
                    const startY = e.clientY;
                    const startPos = { x: this._perspectiveCorners[i].x, y: this._perspectiveCorners[i].y };
                    const onMove = (ev) => {
                        const dx = ev.clientX - startX;
                        const dy = ev.clientY - startY;
                        this._perspectiveCorners[i].x = startPos.x + dx;
                        this._perspectiveCorners[i].y = startPos.y + dy;
                        updateHandlePos();
                        this._applyPerspectiveTransform();
                    };
                    const onUp = () => {
                        hnd.style.cursor = 'grab';
                        document.removeEventListener('pointermove', onMove);
                        document.removeEventListener('pointerup', onUp);
                        this.dbConfig = { ...this.dbConfig, perspectiveOffsets: this._perspectiveOffsetsForDb() };
                        if (this.model?.view?.updateJSON) this.model.view.updateJSON(this.anchor, null);
                    };
                    document.addEventListener('pointermove', onMove);
                    document.addEventListener('pointerup', onUp);
                };
                hnd.addEventListener('pointerdown', onCornerPointerDown);
                perspectiveWrapper.appendChild(hnd);
                this._cornerHandles.push(hnd);
            }
        }

        this._dragHandle = document.createElement('div');
        this._dragHandle.className = 'vapi-button2-drag-handle';
        this._dragHandle.style.cssText = 'position:absolute;top:0;left:0;right:0;height:20px;cursor:grab;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.2);font-size:11px;z-index:5;pointer-events:auto;';
        this._dragHandle.textContent = `⋮⋮ ${this._t('drag-text', 'Drag')}`;
        this._dragHandle.style.display = 'none';

        buttonContent.addEventListener('click', (e) => {
            if (this.state.mode === 'edit') return;
            e.stopPropagation();
            // Allow button to act as an event source in world event/action chains.
            this.model?.view?._executeEventActions?.(this.getId(), 'click');
            this.dispatchCommand();
        });

        root.appendChild(perspectiveWrapper);
        root.appendChild(this._dragHandle);

        this._buttonEl = buttonContent;
        this.root = root;
        this.dom = root;

        const geo = new BoxGeometry(0.1, 0.1, 0.1);
        const mat = new MeshBasicMaterial({ visible: false });
        this.anchor = new Mesh(geo, mat);
        this.anchor.name = 'vapiButton';
        this.anchor.type = 'vapiButton';

        this.elementClass = `vapi-button2d-${this.id}`;
        this.object = this.anchor;
    }

    _syncCornerHandles() {
        const handles = this.dom?.querySelectorAll('.vapi-button2-corner-handle');
        handles?.forEach((hnd, i) => {
            if (this._perspectiveCorners?.[i]) {
                hnd.style.left = `${this._perspectiveCorners[i].x}px`;
                hnd.style.top = `${this._perspectiveCorners[i].y}px`;
            }
        });
    }

    _perspectiveOffsetsForDb() {
        const ew = this.root?.offsetWidth || this._baseWidth || 1;
        const eh = this.root?.offsetHeight || this._baseHeight || 1;
        return (this._perspectiveCorners || []).map(p => ({
            x: p.x,
            y: p.y,
            xr: p.x / ew,
            yr: p.y / eh
        }));
    }

    _setRootPixelSize(newW, newH, { resetPerspective = false, persist = false } = {}) {
        if (!this.root || !Number.isFinite(newW) || !Number.isFinite(newH) || newW <= 0 || newH <= 0) return;
        const oldW = this._baseWidth || 1;
        const oldH = this._baseHeight || 1;
        this._baseWidth = newW;
        this._baseHeight = newH;
        this._originalPos = [{ x: 0, y: 0 }, { x: newW, y: 0 }, { x: newW, y: newH }, { x: 0, y: newH }];

        if (this._perspectiveCorners?.length === 4) {
            if (resetPerspective) {
                this._perspectiveCorners.splice(0, 4, ...this._originalPos.map(p => ({ ...p })));
            } else {
                const sx = oldW > 0 ? (newW / oldW) : 1;
                const sy = oldH > 0 ? (newH / oldH) : 1;
                this._perspectiveCorners.forEach(p => {
                    p.x *= sx;
                    p.y *= sy;
                });
            }
        }

        this.root.style.width = `${newW}px`;
        this.root.style.height = `${newH}px`;
        this.root.style.marginLeft = `-${newW / 2}px`;
        this.root.style.marginTop = `-${newH / 2}px`;
        this._syncCornerHandles();
        this._applyPerspectiveTransform?.();

        this.dbConfig = {
            ...this.dbConfig,
            width: newW,
            height: newH,
            perspectiveOffsets: this._perspectiveOffsetsForDb()
        };
        if (persist && this.model?.view?.updateJSON) this.model.view.updateJSON(this.anchor, null);
    }

    _updateButtonContent(buttonEl, labelText, icon) {
        if (!buttonEl) return;
        buttonEl.innerHTML = '';
        const iconName = (icon || '').trim();
        buttonEl.style.overflow = 'hidden';

        if (iconName) {
            buttonEl.innerHTML = `<span class="material-icons-outlined" aria-hidden="true">${iconName.replace(/</g, '&lt;')}</span><span>${(labelText || this._t('action-text', 'Action')).replace(/</g, '&lt;')}</span>`;
        } else {
            buttonEl.innerHTML = `<span>${(labelText || this._t('action-text', 'Action')).replace(/</g, '&lt;')}</span>`;
        }
        if (this._applyPerspectiveTransform) this._applyPerspectiveTransform();
    }

    _clientToNDC(clientX, clientY) {
        const el = this._viewportEl;
        if (!el) {
            this._ndc.x = (clientX / this._sizes.width) * 2 - 1;
            this._ndc.y = -(clientY / this._sizes.height) * 2 + 1;
            return;
        }
        const rect = el.getBoundingClientRect();
        this._ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this._ndc.y = 1 - ((clientY - rect.top) / rect.height) * 2;
    }

    _setFromClientXY(clientX, clientY) {
        const adjX = clientX - this._dragOffsetX;
        const adjY = clientY - this._dragOffsetY;
        this._clientToNDC(adjX, adjY);
        this._raycaster.setFromCamera(this._ndc, this.camera);
        if (this._raycaster.ray.intersectPlane(this._dragPlane, this._planeIntersectPoint)) {
            const dir = new Vector3().subVectors(this._planeIntersectPoint, this.camera.position).normalize();
            this._tmpVec.copy(this.camera.position).addScaledVector(dir, this._dragStartDistance);
            if (this.anchor.parent) {
                this.anchor.parent.updateMatrixWorld(true);
                this.anchor.parent.worldToLocal(this._tmpVec);
                this.anchor.position.copy(this._tmpVec);
            } else {
                this.anchor.position.copy(this._tmpVec);
            }
        }
    }

    _onPointerMove = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.isDragging) return;
        this._setFromClientXY(e.clientX, e.clientY);
    };

    _onPointerUp = (e) => {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.anchor.parent?.updateMatrixWorld(true);
        this.anchor.getWorldPosition(this._tmpVec);
        const dir = new Vector3().subVectors(this._tmpVec, this.camera.position).normalize();
        this._tmpVec.copy(this.camera.position).addScaledVector(dir, this._dragStartDistance);
        if (this.anchor.parent) {
            this.anchor.parent.worldToLocal(this._tmpVec);
            this.anchor.position.copy(this._tmpVec);
        } else {
            this.anchor.position.copy(this._tmpVec);
        }
        if (this._dragHandle) this._dragHandle.style.cursor = 'grab';
        if (this._captureEl) {
            try { this._captureEl.releasePointerCapture(e.pointerId); } catch (err) { }
            this._captureEl = null;
        }
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
        if (this.controls) this.controls.enabled = true;
        if (this.model?.view) {
            if (this.model.view.updateJSON) this.model.view.updateJSON(this.anchor, null);
            this.model.view.elementSelected = this;
            this.showObjectMenu();
            if (typeof $ !== 'undefined') $('.edit_element').trigger('click');
        }
    };

    _onPointerDown = (e) => {
        if (this.state.mode !== 'edit') return;
        if (e.target?.closest?.('.vapi-button2-corner-handle')) return;
        const rect = this.dom?.getBoundingClientRect?.();
        if (rect && rect.width > 0 && rect.height > 0) {
            const centerX = rect.left + rect.width * 0.5;
            const centerY = rect.top + rect.height * 0.5;
            this._dragOffsetX = e.clientX - centerX;
            this._dragOffsetY = e.clientY - centerY;
        } else {
            this._dragOffsetX = 0;
            this._dragOffsetY = 0;
        }
        this.isDragging = true;
        this._captureEl = e.currentTarget || this.dom;
        try { this._captureEl?.setPointerCapture?.(e.pointerId); } catch (err) { }
        const cameraNormal = new Vector3();
        this.camera.getWorldDirection(cameraNormal);
        if (this.anchor.parent) {
            this.anchor.parent.updateMatrixWorld(true);
            this.anchor.getWorldPosition(this._tmpVec);
            this._dragPlane.setFromNormalAndCoplanarPoint(cameraNormal, this._tmpVec);
        } else {
            this._tmpVec.copy(this.anchor.position);
            this._dragPlane.setFromNormalAndCoplanarPoint(cameraNormal, this._tmpVec);
        }
        this._dragStartDistance = this.camera.position.distanceTo(this._tmpVec);
        if (this._dragHandle) this._dragHandle.style.cursor = 'grabbing';
        window.addEventListener('pointermove', this._onPointerMove);
        window.addEventListener('pointerup', this._onPointerUp);
        window.addEventListener('pointercancel', this._onPointerUp);
        if (this.controls) this.controls.enabled = false;
    };

    _attachDragListeners() {
        if (this._dragHandle) {
            this._dragHandle.addEventListener('pointerdown', this._onPointerDown);
            this._dragHandle.style.cursor = 'grab';
            this._dragHandle.style.display = 'flex';
        }
    }

    _detachDragListeners() {
        if (this._dragHandle) {
            this._dragHandle.removeEventListener('pointerdown', this._onPointerDown);
            this._dragHandle.style.display = 'none';
        }
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
        this.isDragging = false;
        if (this.controls) this.controls.enabled = true;
    }

    render() { }

    getId() {
        return this.id;
    }

    setId(id) {
        this.id = id;
    }

    getDbConfig() {
        return this.dbConfig;
    }

    setDbConfig(config) {
        this.dbConfig = { ...this.dbConfig, ...config };
        this._vapiAgentId = this.dbConfig?.vapiAgentId ?? this._vapiAgentId;
        if (this._buttonEl) {
            const labelText = (this.dbConfig?.commandName ?? this.dbConfig?.label ?? this.dbConfig?.name ?? '').trim() || this._t('command-text', 'Command');
            this._updateButtonContent(this._buttonEl, labelText, this.dbConfig?.icon);
        }
        if (config?.perspectiveOffsets && Array.isArray(config.perspectiveOffsets) && this._perspectiveCorners) {
            const w = this.root?.offsetWidth || this._baseWidth || 1;
            const h = this.root?.offsetHeight || this._baseHeight || 1;
            this._perspectiveCorners.splice(0, 4, ...config.perspectiveOffsets.map(p => p.xr !== undefined
                ? { x: Number(p.xr) * w, y: Number(p.yr) * h }
                : { x: Number(p.x) ?? p.x, y: Number(p.y) ?? p.y }));
            this._syncCornerHandles();
            if (this._applyPerspectiveTransform) this._applyPerspectiveTransform();
        }
        if ((Number(config?.width) > 0 || Number(config?.height) > 0) && this.root) {
            const width = Number(config?.width) > 0 ? Number(config.width) : this._baseWidth;
            const height = Number(config?.height) > 0 ? Number(config.height) : this._baseHeight;
            this._setRootPixelSize(width, height, { resetPerspective: false, persist: false });
        }
        if (this.dom) {
            this.dom.classList.toggle('hide-on-start', !!this.dbConfig?.hideOnStart);
            this.dom.style.display = 'none';
            this.dom.style.pointerEvents = 'none';
            this.dom.style.opacity = '0';
            this.dom.classList.add('hidden-by-action', 'vapi-button-call-inactive');
            this.dom.classList.remove('visible', 'shown-by-action');
        }
    }

    dispatchCommand(commandText = null) {
        const text = String(commandText ?? this.dbConfig?.commandText ?? this.dbConfig?.action ?? '').trim();
        if (!text) return false;
        const chair = this._getOwningChair();
        if (chair?.dispatchCommand) return chair.dispatchCommand(text);
        return false;
    }

    getLimits() {
        return { min: 280, max: 800, distance: 540 };
    }

    getAxisRotations() {
        return { rotateX: 0, rotateY: 0 };
    }

    setDistance(val) { }

    setAxisRotations(axis, angle) { }

    showObjectMenu() {
        $('.object-menu .object-name').text(this._getMenuDisplayName());
        $('.object-menu').addClass('is-active');
        $('.object-menu').addClass('edit');
        $('.vrai-agent-settings').addClass('is-active');
        $('.perspective-settings:not(.vrai-agent-perspective), .tooltip-settings, .video-loop, .iframe-agent-settings').addClass('hidden');
    }

    toggleSelect() {
        this.dom?.querySelector('.vapi-button2-content')?.classList?.toggle('active');
    }

    resetPerspective() {
        if (!this._perspectiveCorners) return;
        this._perspectiveCorners.splice(0, 4, ...this._originalPos.map(p => ({ ...p })));
        this._syncCornerHandles();
        if (this._applyPerspectiveTransform) this._applyPerspectiveTransform();
        this.dbConfig = { ...this.dbConfig, perspectiveOffsets: this._perspectiveOffsetsForDb() };
    }

    destroy() {
        this._disposeVapiConnectionListeners();
        try { this._detachDragListeners(); } catch (e) { }
        if (this.dom?.parentNode) this.dom.parentNode.removeChild(this.dom);
        const dataId = `vapi_button2_${this.id}`;
        document.querySelectorAll(`[data-id='${dataId}']`).forEach(node => node?.remove?.());
    }
}

export { VRVapiButtonWithCSS3D };
